import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import type { ChangeEvent, ReactElement } from 'react'
import type { IFeaturesGroupToEdit } from '~/types/iap'

import ActionsBanner from './ActionsBanner'

import CreateEditFeaturesGroupModal from '../modals/CreateEditFeaturesGroupModal'

import Badge from '~/components/Badge'
import ContentLayout from '~/components/ContentLayout'
import Header from '~/components/Header/Classic'
import IapCard from '~/components/IapCard'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import Tooltip from '~/components/Tooltip'
import ViewTitle from '~/components/ViewTitle'
import { MAX_BADGES_LIMIT } from '~/const/iap'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAppSummary } from '~/providers/AppSummary'
import { iapKeys, useFeaturesGroupListQuery, useFeaturesListQuery } from '~/queries/iap'
import { transformByDeviceType } from '~/utils/application'
import { debounce } from '~/utils/function'

import './styles.scss'

const FeaturesGroupView = (): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const { appDetails, appKey, deviceType, isLoading: isAppSummaryLoading } = useAppSummary()

  const [search, setSearch] = useState('')

  const [selectedFeatureId, setSelectedFeatureId] = useState('')

  const [featuresGroupToEdit, setFeaturesGroupToEdit] = useState<IFeaturesGroupToEdit>()

  const [isModalOpen, setIsModalOpen] = useState(false)

  /** Queries & Mutations */

  const { data: featuresGroup, isLoading: isFeaturesGroupLoading } = useFeaturesGroupListQuery({
    appKey,
    deviceType,
    featureId: selectedFeatureId,
    searchQuery: search,
  })

  const { data: features } = useFeaturesListQuery({
    appKey,
    deviceType,
  })

  const featuresGroupData = featuresGroup?.data

  const featuresOptions = features?.data?.map(({ feature }) => ({
    label: feature.feature_id,
    value: feature.feature_id,
  }))

  /** Handlers */

  const handleSearchChange = useMemo(
    () =>
      debounce((event: ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value)
      }, 500),
    []
  )

  const handleFeatureIdChange = (option?: unknown) => {
    const castedOption = option as IOption | undefined
    setSelectedFeatureId(castedOption?.value || '')
  }

  const handleResetClick = () => {
    setSearch('')
    setSelectedFeatureId('')
    queryClient.invalidateQueries({
      queryKey: iapKeys.featuresGroupList({
        appKey,
        deviceType,
        featureId: '',
        searchQuery: '',
      }),
    })
  }

  const handleModalState = (isOpen: boolean) => () => {
    setIsModalOpen(isOpen)
    !isOpen && setFeaturesGroupToEdit(undefined)
  }

  const handleCreateGroupClick = () => setIsModalOpen(true)

  const handleEditClick = (feature: IFeaturesGroupToEdit) => () => {
    setFeaturesGroupToEdit(feature)
    setIsModalOpen(true)
  }

  /** Effects */

  useDocumentTitle('IAP - Features Group')

  /** Render */

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  return (
    <>
      <Header apps={appSelectorApp ? [appSelectorApp] : []} isSwitchDisabled={isSwitchDisabled} />
      <Layout className="in-apps-features-group-view" withPadding>
        <ViewTitle>
          <span>Features Group</span>
        </ViewTitle>
        <ActionsBanner
          featureOptions={featuresOptions}
          onCreateGroupClick={handleCreateGroupClick}
          onFeatureIdChange={handleFeatureIdChange}
          onResetClick={handleResetClick}
          onSearchChange={handleSearchChange}
          search={search}
          selectedFeatureId={selectedFeatureId}
        />
        <ContentLayout loading={isAppSummaryLoading || isFeaturesGroupLoading}>
          <Section className="in-apps-features-group-view__features-group-wrapper">
            {featuresGroupData && featuresGroupData.length > 0 ? (
              featuresGroupData.map(({ feature_group_id, feature_ids, name }) => (
                <IapCard
                  content={
                    feature_ids ? (
                      <div className="iap-card-badge-container">
                        {feature_ids.slice(0, MAX_BADGES_LIMIT).map((id) => (
                          <Badge className="in-app-badge" key={id} outlined value={id} />
                        ))}
                        {feature_ids?.length > MAX_BADGES_LIMIT ? (
                          <Tooltip
                            align={{ points: ['br', 'tr'] }}
                            overlay={
                              <div className="iap-card-badge-tooltip-content">
                                {feature_ids
                                  ?.slice(MAX_BADGES_LIMIT)
                                  .map((id) => <Badge className="in-app-badge" key={id} outlined value={id} />)}
                              </div>
                            }
                            placement="top"
                            trigger="hover"
                          >
                            <div className="iap-card-badge-container__items-counter">
                              +{feature_ids?.length - MAX_BADGES_LIMIT}
                            </div>
                          </Tooltip>
                        ) : null}
                      </div>
                    ) : (
                      <div className="no-feature-wrapper">
                        <span className="no-feature-wrapper__message">No Features in this group</span>
                      </div>
                    )
                  }
                  key={feature_group_id}
                  onEditClick={handleEditClick({ featureGroupId: feature_group_id, featureIds: feature_ids, name })}
                  titleComponent={name}
                  type="edit"
                />
              ))
            ) : (
              <div>No data available for this app</div>
            )}
          </Section>
        </ContentLayout>
      </Layout>
      <CreateEditFeaturesGroupModal
        featureGroupToEdit={featuresGroupToEdit}
        featuresOptions={featuresOptions}
        isOpen={isModalOpen}
        onClose={handleModalState(false)}
        params={{
          appKey,
          deviceType,
          featureId: selectedFeatureId,
          searchQuery: search,
        }}
      />
    </>
  )
}

export default FeaturesGroupView
